import React from "react";
import "./tailwindstyles.css";
import {
  FaBolt,
  FaRocket,
  FaUsers,
  FaArrowRight,
  FaLinkedin,
  FaGithub,
  FaUpload,
} from "react-icons/fa";

const CareersPage = () => {
  return (
    <div className="relative min-h-screen overflow-hidden text-white">

      {/* ================= BACKGROUND ================= */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-[#0b0f19] to-[#111827] opacity-95"></div>

      {/* ================= GLOW EFFECTS ================= */}
      <div className="absolute inset-0 opacity-40 pointer-events-none">

        <div
          className="absolute top-0 left-0 w-[120%] h-[120%]
          bg-gradient-to-r from-orange-500/30 via-yellow-400/20 to-sky-500/30
          blur-3xl animate-slowWave"
        ></div>

        <div
          className="absolute bottom-0 right-0 w-[120%] h-[120%]
          bg-gradient-to-r from-sky-500/20 via-orange-300/20 to-yellow-400/30
          blur-3xl animate-fastWave"
        ></div>

      </div>

      {/* ================= GRID ================= */}
      <div
        className="pointer-events-none absolute inset-0
        bg-[linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),
             linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)]
        bg-[size:60px_60px]"
      />

      {/* ================= FLOATING PARTICLES ================= */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">

        {[...Array(20)].map((_, i) => (
          <span
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${12 + Math.random() * 10}s`,
              width: `${4 + Math.random() * 6}px`,
              height: `${4 + Math.random() * 6}px`,
            }}
          />
        ))}

      </div>

      {/* ================= CONTENT ================= */}
      <div className="relative z-10 pb-24">

        {/* ================= HERO ================= */}
        <header className="relative text-center pt-28 pb-20 px-6">

          {/* Badge */}
          <div
            className="
              inline-flex items-center gap-3
              px-6 py-3 rounded-full
              border border-white/10
              bg-white/[0.04]
              backdrop-blur-xl
              shadow-[0_10px_40px_rgba(0,0,0,0.35)]
              mb-8
              animate-fade-up
            "
          >

            <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse"></span>

            <p className="uppercase tracking-[4px] text-orange-300 text-[11px] font-medium">
              Careers at Power Sense
            </p>

          </div>

          {/* Heading */}
          <h1
            className="
              text-4xl md:text-6xl
              font-black
              leading-[1]
              tracking-tight
              animate-fade-up
            "
          >

            Build the Future of
            <br />

            <span
              className="
                bg-gradient-to-r
                from-orange-300
                via-yellow-200
                to-cyan-300
                bg-clip-text text-transparent
                animate-gradientMove
                bg-[length:200%_200%]
              "
            >
              Smart Energy
            </span>

          </h1>

          {/* Description */}
          <p
            className="
              mt-6 max-w-3xl mx-auto
              text-slate-400
              text-sm md:text-lg
              leading-relaxed
              animate-fade-up-delay
            "
          >
            Join Power Sense and work on real-world IoT systems,
            intelligent energy analytics, smart automation, and
            next-generation infrastructure technologies powering industries,
            campuses, and smart cities.
          </p>

        </header>

        {/* ================= WHY JOIN ================= */}
        <section className="max-w-7xl mx-auto px-6">

          <div className="text-center mb-14">

            <h2
              className="
                text-3xl md:text-4xl
                font-black
                text-white
                animate-fade-up
              "
            >
              Why Join Us?
            </h2>

            <p
              className="
                mt-4 max-w-2xl mx-auto
                text-slate-400
                text-sm md:text-base
                leading-relaxed
              "
            >
              At Power Sense, innovation meets real-world impact.
              Work with cutting-edge technologies and help shape
              the future of intelligent energy systems.
            </p>

          </div>

          {/* Cards */}
          <div className="grid md:grid-cols-3 gap-7">

            {[
              {
                icon: <FaRocket />,
                title: "Innovative Environment",
                description:
                  "Work with smart grids, IoT platforms, real-time monitoring and AI-powered analytics.",
                glow:
                  "from-orange-500/20 via-yellow-400/10 to-transparent",
              },

              {
                icon: <FaUsers />,
                title: "Growth & Mentorship",
                description:
                  "Learn directly from experienced mentors and industry professionals in energy technology.",
                glow:
                  "from-sky-500/20 via-cyan-400/10 to-transparent",
              },

              {
                icon: <FaBolt />,
                title: "Real-World Impact",
                description:
                  "Build technologies that directly improve industries, campuses and communities.",
                glow:
                  "from-green-500/20 via-emerald-400/10 to-transparent",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="
                  group relative overflow-hidden
                  rounded-3xl
                  border border-white/10
                  bg-gradient-to-br from-orange-400/[0.60] via-yellow-300/[0.45] to-cyan-400/[0.35]
                  backdrop-blur-2xl
                  p-8
                  hover:-translate-y-3
                  hover:border-white/20
                  transition-all duration-500
                  shadow-[0_10px_50px_rgba(0,0,0,0.35)]
                  animate-fade-up
                "
              >

                {/* Card Inner Glow */}
                <div
                  className="
                    absolute inset-0
                    opacity-40
                    bg-gradient-to-br
                    from-orange-300/[0.35]
                    via-yellow-200/[0.25]
                    to-sky-300/[0.20]
                    blur-2xl
                    pointer-events-none
                  "
                ></div>
               

                {/* Hover Glow */}
                <div
                  className={`
                    absolute inset-0 opacity-0
                    group-hover:opacity-30
                    transition-all duration-700
                    bg-gradient-to-r ${item.glow}
                    blur-3xl
                  `}
                />

                {/* Top Glow Line */}
                <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>

                {/* Icon */}
                <div
                  className="
                    relative z-10
                    w-14 h-14 rounded-2xl
                    bg-white/[0.08]
                    border border-white/10
                    flex items-center justify-center
                    text-white text-xl
                    mb-6
                    group-hover:scale-110 group-hover:rotate-6
                    transition-all duration-500
                  "
                >
                  {item.icon}
                </div>

                {/* Title */}
                <h3 className="relative z-10 text-xl font-bold text-white">
                  {item.title}
                </h3>

                {/* Description */}
                <p className="relative z-10 mt-4 text-slate-300 leading-relaxed text-sm">
                  {item.description}
                </p>

              </div>
            ))}

          </div>
        </section>

        {/* ================= FORM SECTION ================= */}
        <section className="max-w-4xl mx-auto px-6 mt-24">

          {/* Section Header */}
          <div className="text-center mb-12">

            <h2
              className="
                text-3xl md:text-4xl
                font-black
                text-white
              "
            >
              Submit Your Resume
            </h2>

            <p
              className="
                mt-4 max-w-2xl mx-auto
                text-slate-400
                text-sm md:text-base
                leading-relaxed
              "
            >
              Whether you're an engineer, developer, analyst,
              or passionate innovator — we’d love to hear from you.
            </p>

          </div>

          {/* Form Card */}
          <div
            className="
              relative overflow-hidden
              rounded-[2rem]
              border border-white/10
              bg-gradient-to-br from-white/[0.08] via-white/[0.04] to-orange-400/[0.03]
              backdrop-blur-2xl
              p-8 md:p-10
              shadow-[0_15px_70px_rgba(0,0,0,0.45)]
            "
          >

            {/* Card Inner Glow */}
            <div
              className="
                absolute inset-0
                opacity-30
                bg-gradient-to-br
                from-orange-300/[0.08]
                via-yellow-200/[0.03]
                to-sky-300/[0.05]
                blur-2xl
                pointer-events-none
              "
            ></div>

            {/* Glow */}
            <div
              className="
                absolute inset-0
                bg-gradient-to-r
                from-orange-500/10
                via-yellow-400/5
                to-sky-500/10
                blur-3xl
              "
            ></div>

            {/* Top Line */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-orange-400 via-yellow-300 to-cyan-400"></div>

            <form className="relative z-10">

              {/* Grid */}
              <div className="grid md:grid-cols-2 gap-6">

                {/* Full Name */}
                <div>
                  <label className="block text-sm text-slate-300 mb-2">
                    Full Name
                  </label>

                  <input
                    type="text"
                    placeholder="Enter your full name"
                    className="
                      w-full
                      bg-black/20
                      border border-white/10
                      rounded-2xl
                      px-5 py-4
                      text-white
                      placeholder:text-slate-500
                      focus:border-orange-400/40
                      focus:bg-black/30
                      outline-none
                      transition-all duration-300
                    "
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm text-slate-300 mb-2">
                    Email Address
                  </label>

                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="
                      w-full
                      bg-black/20
                      border border-white/10
                      rounded-2xl
                      px-5 py-4
                      text-white
                      placeholder:text-slate-500
                      focus:border-sky-400/40
                      focus:bg-black/30
                      outline-none
                      transition-all duration-300
                    "
                  />
                </div>

              </div>

              {/* Profiles */}
              <div className="grid md:grid-cols-2 gap-6 mt-6">

                {/* LinkedIn */}
                <div>
                  <label className="block text-sm text-slate-300 mb-2">
                    LinkedIn Profile
                  </label>

                  <div className="relative">

                    <FaLinkedin className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500" />

                    <input
                      type="url"
                      placeholder="linkedin.com/in/username"
                      className="
                        w-full
                        bg-black/20
                        border border-white/10
                        rounded-2xl
                        pl-14 pr-5 py-4
                        text-white
                        placeholder:text-slate-500
                        focus:border-blue-400/40
                        focus:bg-black/30
                        outline-none
                        transition-all duration-300
                      "
                    />

                  </div>
                </div>

                {/* GitHub */}
                <div>
                  <label className="block text-sm text-slate-300 mb-2">
                    GitHub Profile
                  </label>

                  <div className="relative">

                    <FaGithub className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500" />

                    <input
                      type="url"
                      placeholder="github.com/username"
                      className="
                        w-full
                        bg-black/20
                        border border-white/10
                        rounded-2xl
                        pl-14 pr-5 py-4
                        text-white
                        placeholder:text-slate-500
                        focus:border-green-400/40
                        focus:bg-black/30
                        outline-none
                        transition-all duration-300
                      "
                    />

                  </div>
                </div>

              </div>

              {/* Resume Upload */}
              <div className="mt-6">

                <label className="block text-sm text-slate-300 mb-2">
                  Upload Resume
                </label>

                <div
                  className="
                    relative
                    border border-dashed border-white/15
                    rounded-3xl
                    bg-black/20
                    p-10
                    text-center
                    hover:border-yellow-400/30
                    hover:bg-black/30
                    transition-all duration-500
                  "
                >

                  <FaUpload className="mx-auto text-3xl text-yellow-300 mb-4" />

                  <p className="text-slate-300 text-sm">
                    Upload PDF, DOC or DOCX
                  </p>

                  <p className="text-slate-500 text-xs mt-2">
                    Drag & Drop or click to upload
                  </p>

                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />

                </div>

              </div>

              {/* Button */}
              <div className="mt-10 text-center">

                <button
                  type="submit"
                  className="
                    group relative overflow-hidden
                    px-10 py-4 rounded-2xl
                    bg-gradient-to-r from-orange-500 via-yellow-400 to-orange-500
                    text-black font-bold
                    hover:scale-105
                    transition-all duration-500
                    shadow-[0_10px_40px_rgba(255,196,0,0.25)]
                  "
                >

                  <span className="relative z-10 flex items-center">

                    Submit Application

                    <FaArrowRight
                      className="
                        ml-3
                        transition-transform duration-500
                        group-hover:translate-x-1
                      "
                    />

                  </span>

                  {/* Button Glow */}
                  <div
                    className="
                      absolute inset-0 opacity-0
                      group-hover:opacity-20
                      transition-all duration-700
                      bg-white blur-2xl
                    "
                  ></div>

                </button>

              </div>

            </form>

          </div>

        </section>

      </div>

      {/* ================= ANIMATIONS ================= */}
      <style>{`

        @keyframes slowWave {
          0% {
            transform: translate(-10%, -10%) rotate(0deg);
          }

          50% {
            transform: translate(10%, 10%) rotate(20deg);
          }

          100% {
            transform: translate(-10%, -10%) rotate(0deg);
          }
        }

        @keyframes fastWave {
          0% {
            transform: translate(10%, 10%) rotate(0deg);
          }

          50% {
            transform: translate(-10%, -10%) rotate(-20deg);
          }

          100% {
            transform: translate(10%, 10%) rotate(0deg);
          }
        }

        @keyframes gradientMove {
          0% {
            background-position: 0% 50%;
          }

          50% {
            background-position: 100% 50%;
          }

          100% {
            background-position: 0% 50%;
          }
        }

        @keyframes particleFloat {

          0% {
            transform: translateY(100vh) scale(0);
            opacity: 0;
          }

          10% {
            opacity: 0.4;
          }

          50% {
            opacity: 0.8;
          }

          100% {
            transform: translateY(-120vh) scale(1);
            opacity: 0;
          }
        }

        @keyframes fadeUp {

          0% {
            opacity: 0;
            transform: translateY(30px);
          }

          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slowWave {
          animation: slowWave 14s ease-in-out infinite;
        }

        .animate-fastWave {
          animation: fastWave 10s ease-in-out infinite;
        }

        .animate-gradientMove {
          animation: gradientMove 6s ease infinite;
        }

        .animate-fade-up {
          animation: fadeUp 1s ease forwards;
        }

        .animate-fade-up-delay {
          animation: fadeUp 1.2s ease forwards;
        }

        .particle {
          position: absolute;
          bottom: -20px;
          border-radius: 999px;

          background:
            radial-gradient(circle,
            rgba(255,255,255,0.9) 0%,
            rgba(249,115,22,0.6) 40%,
            transparent 80%);

          filter: blur(1px);

          animation-name: particleFloat;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }

      `}</style>

    </div>
  );
};

export default CareersPage;